import { Instance, SnapshotOut, types } from 'mobx-state-tree';
import { commonApi } from 'src/service';
import { DYNAMIC_TYPE_CODE } from 'src/static/constants';

import { CodeGroupModel, ICodeGroupModel, LabelValueModel } from '../common-code/CommonCode';
import { DynamicCode, IDynamicCode } from '../dynamic-code/DynamicCode';

/**
 * # CommonCodeStore
 * - 공통코드, 다이나믹코드 목록을 관리하는 스토어
 */
export const CommonCodeStore = types
  .model('CommonCodeStore')
  .props({
    commonCodes: types.optional(types.array(CodeGroupModel), []),
    dynamicCodes: types.optional(types.array(DynamicCode), []),
    // 공지사항 유형
    noticeSelectOptions: types.optional(types.array(LabelValueModel), []),
    // 뉴스 유형
    newsSelectOptions: types.optional(types.array(LabelValueModel), []),
    // 지원 유형
    applySelectOptions: types.optional(types.array(LabelValueModel), []),
  })
  .views((self) => ({
    getCCodeByName(name: string) {
      const result = self.commonCodes.find((item) => item.name === name);
      if (result) {
        return result.list.map((item) => ({ label: item.value, value: item.code }));
      }
    },
    getDynamicCodeById(id: number) {
      const results = self.dynamicCodes.filter((item) => item.dynamicCd?.code === id);
      if (results.length > 0) {
        return results.map((item) => ({
          label: item.value ?? '',
          value: item.cdId,
        }));
      }
      return [];
    },
  }))
  // 다이나믹 코드 select options 형태의 데이터로 가공하는 공통 함수
  .actions((self) => ({
    setSelectOptions: (codeList: IDynamicCode[], typeCode: number, targetArray: any) => {
      const selectOptions = codeList
        .filter((item) => item.dynamicCd?.code === typeCode)
        .map((item) => ({ label: item.value || '', value: item.cdId || 0 }));
      targetArray.replace(selectOptions);
    },
  }))
  // 각 유형별 select options 설정 + 공통 코드, 다이나믹 코드 저장
  .actions((self) => ({
    // 공지사항 유형 설정
    setNoticeSelectOptions: (codeList: IDynamicCode[]) => {
      self.setSelectOptions(codeList, DYNAMIC_TYPE_CODE.NOTICE, self.noticeSelectOptions);
    },

    // 뉴스 유형 설정
    setNewsSelectOptions: (codeList: IDynamicCode[]) => {
      self.setSelectOptions(codeList, DYNAMIC_TYPE_CODE.NEWS, self.newsSelectOptions);
    },

    // 채용지원서 - 지원 유형 설정
    setApplySelectOptions: (codeList: IDynamicCode[]) => {
      self.setSelectOptions(codeList, DYNAMIC_TYPE_CODE.APPLY, self.applySelectOptions);
    },

    setCommonCodes: (commonCodes: ICodeGroupModel[]) => {
      self.commonCodes.replace(commonCodes);
    },

    setDynamicCodes: (dynamicCodes: IDynamicCode[]) => {
      self.dynamicCodes.replace(dynamicCodes);
    },
  }))
  // 공통 코드, 다이나믹 코드 조회
  .actions((self) => ({
    getCommonCodes: async () => {
      const result = await commonApi.getCommonCodes();
      if (result) {
        self.setCommonCodes(result);
      }
    },
    getDynamicCodes: async (dynamicCd?: number) => {
      const result = await commonApi.getDynamicCodes(dynamicCd);

      if (result) {
        self.setDynamicCodes(result);
        self.setNoticeSelectOptions(result);
        self.setNewsSelectOptions(result);
        self.setApplySelectOptions(result);
      }
    },
  }))
  .actions((self) => ({
    // 모델 생성 시 공통코드 조회
    afterCreate() {
      self.getCommonCodes();
      self.getDynamicCodes();
    },
  }));

// --------------------------------------------------------------------------
type TCommonCodeStore = Instance<typeof CommonCodeStore>;
type TCommonCodeStoreSnapshot = SnapshotOut<typeof CommonCodeStore>;

export interface ICommonCodeStore extends TCommonCodeStore {}
export type TCommonCodeStoreKeys = keyof TCommonCodeStoreSnapshot & string;
export interface ICommonCodeStoreSnapshot extends TCommonCodeStoreSnapshot {}
export const createCommonCodeStore = () => types.optional(CommonCodeStore, {} as TCommonCodeStore);
